import type { FormValue } from '../../types';
import type { RepeatBindings } from '../primitives/repeatBindings';
import { evaluateRepeatItemEq } from '../primitives/repeatItemEq';
import type { ConditionalWhenClause } from './types';

/** Resolve `derived[id]` from layout `sum` nodes; unknown ids behave as `0`. */
export function lookupDerivedNumeric(
  derived: Readonly<Record<string, number>>,
  id: string
): number {
  const v = derived[id];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** True when a single `conditional` / `disabled-when` clause matches current answers and repeat scope. */
export function whenClauseSatisfied(
  when: ConditionalWhenClause,
  answers: Record<string, FormValue>,
  repeatBindings: RepeatBindings,
  derived: Readonly<Record<string, number>> = {}
): boolean {
  if ('kind' in when && when.kind === 'repeatItemEq') {
    return evaluateRepeatItemEq(when.binding, when.eq, repeatBindings);
  }

  if ('derived' in when && typeof when.derived === 'string') {
    const n = lookupDerivedNumeric(derived, when.derived);
    if ('between' in when && when.between != null) {
      const {
        min,
        max,
        minInclusive = true,
        maxInclusive = false,
      } = when.between;
      const geMin = minInclusive ? n >= min : n > min;
      const leMax = maxInclusive ? n <= max : n < max;
      return geMin && leMax;
    }
    if ('lt' in when) return n < when.lt;
    if ('lte' in when) return n <= when.lte;
    if ('gt' in when) return n > when.gt;
    if ('gte' in when) return n >= when.gte;
    return false;
  }

  if ('includes' in when) {
    const val = answers[when.qid];
    return Array.isArray(val) && val.includes(when.includes);
  }
  if ('qid' in when && 'eq' in when) {
    return answers[when.qid] === when.eq;
  }
  return false;
}
